import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, User, Mail, Phone, Building2, Briefcase, Shield,
  BookOpen, Clock, CheckCircle2, XCircle, AlertTriangle, Trophy,
  Calendar, Target, Award, Activity, Loader2, ToggleLeft, ToggleRight,
  Edit, KeyRound, Send, Plus, Search, Check, GraduationCap
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { AdminLayout } from "../../components/layout/AdminLayout";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, AreaChart, Area
} from "recharts";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const statusColors = {
  active: "bg-green-500/10 text-green-600 border-green-500/20",
  inactive: "bg-red-500/10 text-red-600 border-red-500/20",
  offboarding: "bg-amber-500/10 text-amber-600 border-amber-500/20",
};

const roleLabels = {
  super_admin: "Super Admin", tenant_admin: "Business Admin",
  trainer: "Trainer", learner: "Learner", guest: "Guest", manager: "Manager",
};

const DONUT_COLORS = ["#10b981", "#3b82f6", "#9ca3af"];

const EmployeeProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showResetPwd, setShowResetPwd] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [allCourses, setAllCourses] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState(new Set());
  const [courseSearch, setCourseSearch] = useState("");
  const [enrolling, setEnrolling] = useState(false);
  const [coursesLoading, setCoursesLoading] = useState(false);

  useEffect(() => { fetchProfile(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProfile = async () => {
    try {
      const res = await api.get(`/users/${userId}/profile`);
      setProfile(res.data);
    } catch (e) {
      toast.error("Failed to load profile");
    } finally { setLoading(false); }
  };

  const handleStatusToggle = async (newStatus) => {
    try {
      await api.put(`/users/${userId}/status`, { status: newStatus });
      toast.success(`User status set to ${newStatus}`);
      fetchProfile();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openEditDialog = () => {
    if (!profile) return;
    const u = profile.user;
    setEditForm({
      full_name: u.full_name || "", role: u.role || "learner",
      department: u.department || "", job_title: u.job_title || "",
      phone: u.phone || "", manager_id: u.manager_id || "",
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${userId}`, editForm);
      toast.success("User updated successfully");
      setShowEditDialog(false);
      fetchProfile();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
    finally { setSaving(false); }
  };

  const handleResetPassword = async () => {
    setSaving(true);
    try {
      await api.post(`/users/${userId}/reset-password`);
      toast.success("Password reset email sent (simulated)");
      setShowResetPwd(false);
    } catch (e) { toast.error("Failed to reset password"); }
    finally { setSaving(false); }
  };

  const openEnrollModal = async () => {
    setShowEnrollModal(true);
    setSelectedCourses(new Set());
    setCourseSearch("");
    setCoursesLoading(true);
    try {
      const res = await api.get("/courses");
      setAllCourses(res.data.courses || res.data || []);
    } catch (e) { toast.error("Failed to load courses"); }
    finally { setCoursesLoading(false); }
  };

  const toggleCourseSelection = (courseId) => {
    setSelectedCourses(prev => {
      const next = new Set(prev);
      if (next.has(courseId)) next.delete(courseId);
      else next.add(courseId);
      return next;
    });
  };

  const handleEnrollCourses = async () => {
    if (selectedCourses.size === 0) return;
    setEnrolling(true);
    let success = 0;
    let failed = 0;
    for (const courseId of selectedCourses) {
      try {
        await api.post("/enrollments", { user_id: userId, course_id: courseId });
        success++;
      } catch (e) {
        failed++;
      }
    }
    setEnrolling(false);
    setShowEnrollModal(false);
    if (success > 0) toast.success(`Enrolled in ${success} course${success > 1 ? "s" : ""}`);
    if (failed > 0) toast.error(`${failed} enrollment${failed > 1 ? "s" : ""} failed (may already be enrolled)`);
    fetchProfile();
  };

  const variant = currentUser?.role === "super_admin" ? "super_admin" : "tenant_admin";

  if (loading) return <AdminLayout title="Employee Profile" variant={variant}><div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></AdminLayout>;
  if (!profile) return <AdminLayout title="Employee Profile" variant={variant}><div className="flex items-center justify-center py-20"><p>User not found</p></div></AdminLayout>;

  const { user: u, stats: s, charts: ch, enrollments, assessment_attempts, compliance, sessions } = profile;

  return (
    <AdminLayout title={u.full_name || "Employee Profile"} variant={variant}>
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to Team
        </Button>

        {/* Profile Header */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-start gap-5">
                <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center text-xl font-bold text-primary shrink-0">
                  {u.full_name?.charAt(0) || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h1 className="text-xl font-bold truncate" data-testid="employee-name">{u.full_name}</h1>
                    <Badge className={statusColors[u.status || "active"]}>{u.status || "active"}</Badge>
                    <Badge variant="outline">{roleLabels[u.role] || u.role}</Badge>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1.5 truncate"><Mail className="w-3.5 h-3.5 shrink-0" />{u.email}</span>
                    {u.phone && <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 shrink-0" />{u.phone}</span>}
                    {u.department && <span className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 shrink-0" />{u.department}</span>}
                    {u.job_title && <span className="flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5 shrink-0" />{u.job_title}</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1.5">Member since {new Date(u.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" onClick={openEditDialog} data-testid="edit-profile-btn">
                    <Edit className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowResetPwd(true)} data-testid="reset-pwd-btn">
                    <KeyRound className="w-3.5 h-3.5 mr-1" /> Reset
                  </Button>
                  {(u.status || "active") === "active" ? (
                    <Button size="sm" variant="outline" className="text-destructive border-destructive/30" onClick={() => handleStatusToggle("inactive")} data-testid="deactivate-btn">
                      <XCircle className="w-3.5 h-3.5 mr-1" /> Deactivate
                    </Button>
                  ) : (
                    <Button size="sm" onClick={() => handleStatusToggle("active")} data-testid="activate-btn">
                      <ToggleRight className="w-3.5 h-3.5 mr-1" /> Activate
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {[
            { label: "Courses", value: s.total_enrollments, icon: BookOpen, color: "text-blue-500" },
            { label: "Completed", value: s.completed_courses, icon: CheckCircle2, color: "text-green-500" },
            { label: "In Progress", value: s.in_progress_courses, icon: Clock, color: "text-amber-500" },
            { label: "Learning Hours", value: s.total_learning_hours, icon: Activity, color: "text-purple-500" },
            { label: "Avg Score", value: `${s.avg_assessment_score}%`, icon: Target, color: "text-primary" },
            { label: "Points", value: s.points, icon: Trophy, color: "text-amber-500" },
          ].map((st, i) => (
            <Card key={i}>
              <CardContent className="p-3 text-center">
                <st.icon className={`w-5 h-5 mx-auto mb-1 ${st.color}`} />
                <p className="text-lg font-bold">{st.value}</p>
                <p className="text-[10px] text-muted-foreground">{st.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Analytics Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Completion Rate Donut */}
          <Card data-testid="chart-completion-donut">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-primary" /> Course Completion
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(ch?.completion_donut || []).some(d => d.value > 0) ? (
                <div className="flex items-center gap-4">
                  <ResponsiveContainer width="55%" height={180}>
                    <PieChart>
                      <Pie data={ch.completion_donut.filter(d => d.value > 0)} cx="50%" cy="50%" innerRadius={50} outerRadius={75} dataKey="value" paddingAngle={3}>
                        {ch.completion_donut.filter(d => d.value > 0).map((_, i) => <Cell key={i} fill={DONUT_COLORS[i]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-2">
                    {ch.completion_donut.filter(d => d.value > 0).map((d, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: DONUT_COLORS[i] }} />
                        <span className="text-sm">{d.name}: <strong>{d.value}</strong></span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-center py-8 text-sm text-muted-foreground">No enrollment data</p>
              )}
            </CardContent>
          </Card>

          {/* Skill Category Radar */}
          <Card data-testid="chart-skill-radar">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Target className="w-4 h-4 text-primary" /> Skill Proficiency by Category
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(ch?.skill_radar || []).length > 0 ? (
                <ResponsiveContainer width="100%" height={180}>
                  <RadarChart data={ch.skill_radar}>
                    <PolarGrid stroke="#e2e8f0" />
                    <PolarAngleAxis dataKey="category" tick={{ fontSize: 10 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 9 }} />
                    <Radar name="Proficiency" dataKey="score" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.25} strokeWidth={2} />
                    <Tooltip formatter={(v) => `${v}%`} />
                  </RadarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-center py-8 text-sm text-muted-foreground">No category data</p>
              )}
            </CardContent>
          </Card>

          {/* Learning Activity Trend */}
          <Card data-testid="chart-monthly-activity">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Activity className="w-4 h-4 text-primary" /> Learning Activity (6 Months)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={ch?.monthly_activity || []}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="courses" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.1} strokeWidth={2} name="Enrolled" />
                  <Area type="monotone" dataKey="completed" stroke="#10b981" fill="#10b981" fillOpacity={0.1} strokeWidth={2} name="Completed" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Assessment Scores */}
          <Card data-testid="chart-assessment-scores">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Award className="w-4 h-4 text-primary" /> Assessment Scores
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(ch?.assessment_scores || []).length > 0 ? (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={ch.assessment_scores} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => `${v}%`} />
                    <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                      {ch.assessment_scores.map((entry, i) => (
                        <Cell key={i} fill={entry.passed ? "#10b981" : "#ef4444"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-center py-8 text-sm text-muted-foreground">No assessment data</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Compliance Timeline */}
        {(ch?.compliance_timeline || []).length > 0 && (
          <Card data-testid="chart-compliance-timeline">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Shield className="w-4 h-4 text-primary" /> Compliance Timeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative pl-6">
                <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-border" />
                {ch.compliance_timeline.map((item, i) => {
                  const color = item.status === "completed" ? "bg-green-500" : item.status === "overdue" ? "bg-red-500" : "bg-amber-500";
                  return (
                    <div key={i} className="relative flex items-start gap-4 mb-4 last:mb-0">
                      <div className={`absolute left-[-17px] top-1.5 w-3 h-3 rounded-full border-2 border-background ${color}`} />
                      <div className="flex-1 flex items-center justify-between p-3 rounded-lg border">
                        <div>
                          <p className="text-sm font-medium">{item.title}</p>
                          {item.due_date && <p className="text-xs text-muted-foreground">Due: {item.due_date}</p>}
                        </div>
                        <Badge variant="outline" className={
                          item.status === "completed" ? "text-green-600 border-green-500/20" : 
                          item.status === "overdue" ? "text-red-600 border-red-500/20" : 
                          "text-amber-600 border-amber-500/20"
                        }>
                          {item.status}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Detail Tabs */}
        <Tabs defaultValue="courses" className="w-full">
          <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
            <TabsTrigger value="courses" data-testid="tab-courses">Courses</TabsTrigger>
            <TabsTrigger value="compliance" data-testid="tab-compliance">Compliance</TabsTrigger>
            <TabsTrigger value="assessments" data-testid="tab-assessments">Assessments</TabsTrigger>
            <TabsTrigger value="sessions" data-testid="tab-sessions">Sessions</TabsTrigger>
          </TabsList>

          <TabsContent value="courses" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2"><BookOpen className="w-5 h-5 text-primary" /> Course Enrollments ({enrollments.length})</CardTitle>
                  <Button size="sm" onClick={openEnrollModal} data-testid="enroll-course-btn">
                    <Plus className="w-4 h-4 mr-1" /> Enroll in Course
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {enrollments.length === 0 ? <p className="text-center py-8 text-muted-foreground">No course enrollments</p> : (
                  <div className="space-y-2">
                    {enrollments.map((e, i) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-lg border">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${e.status === "completed" ? "bg-green-500/10" : "bg-blue-500/10"}`}>
                            {e.status === "completed" ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <BookOpen className="w-4 h-4 text-blue-500" />}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{e.course_title}</p>
                            <p className="text-xs text-muted-foreground">{e.course_type?.includes("scorm") ? "eLearning" : e.course_type}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <div className="w-20">
                            <Progress value={e.progress || 0} className="h-1.5" />
                            <p className="text-[10px] text-muted-foreground text-right mt-0.5">{e.progress || 0}%</p>
                          </div>
                          <Badge className={e.status === "completed" ? "bg-green-500/10 text-green-600" : e.status === "in_progress" ? "bg-blue-500/10 text-blue-600" : "bg-muted text-muted-foreground"} variant="outline">
                            {e.status?.replace("_", " ")}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="compliance" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary" /> Compliance ({compliance.length})
                  <div className="flex gap-2 ml-auto text-xs">
                    <Badge className="bg-green-500/10 text-green-600">{s.compliance_completed} Done</Badge>
                    {s.compliance_overdue > 0 && <Badge className="bg-red-500/10 text-red-600">{s.compliance_overdue} Overdue</Badge>}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {compliance.length === 0 ? <p className="text-center py-8 text-muted-foreground">No compliance assignments</p> : (
                  <div className="space-y-2">
                    {compliance.map((c, i) => (
                      <div key={i} className={`flex items-center justify-between p-3 rounded-lg border ${c.status === "overdue" ? "border-red-500/20 bg-red-500/5" : ""}`}>
                        <div className="flex items-center gap-3">
                          {c.status === "completed" ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : c.status === "overdue" ? <AlertTriangle className="w-4 h-4 text-red-500" /> : <Clock className="w-4 h-4 text-amber-500" />}
                          <div>
                            <p className="text-sm font-medium">{c.template_title}</p>
                            {c.due_date && <p className="text-xs text-muted-foreground">Due: {c.due_date}</p>}
                          </div>
                        </div>
                        <Badge variant="outline" className={c.status === "completed" ? "text-green-600" : c.status === "overdue" ? "text-red-600" : "text-amber-600"}>
                          {c.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="assessments" className="mt-4">
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Target className="w-5 h-5 text-primary" /> Assessment Attempts ({assessment_attempts.length})</CardTitle></CardHeader>
              <CardContent>
                {assessment_attempts.length === 0 ? <p className="text-center py-8 text-muted-foreground">No assessment attempts</p> : (
                  <div className="space-y-2">
                    {assessment_attempts.map((a, i) => (
                      <div key={i} className={`flex items-center justify-between p-3 rounded-lg border ${a.passed ? "bg-green-500/5 border-green-500/20" : "bg-red-500/5 border-red-500/20"}`}>
                        <div className="flex items-center gap-3">
                          {a.passed ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
                          <div>
                            <p className="text-sm font-medium">{a.assessment_title}</p>
                            <p className="text-xs text-muted-foreground">Attempt #{a.attempt_number} - {Math.floor(a.time_spent_seconds / 60)}m</p>
                          </div>
                        </div>
                        <Badge className={a.passed ? "bg-green-500/10 text-green-600" : "bg-red-500/10 text-red-600"}>{a.score}%</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sessions" className="mt-4">
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Calendar className="w-5 h-5 text-primary" /> ILT Sessions ({sessions.length})</CardTitle></CardHeader>
              <CardContent>
                {sessions.length === 0 ? <p className="text-center py-8 text-muted-foreground">No session registrations</p> : (
                  <div className="space-y-2">
                    {sessions.map((se, i) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-lg border">
                        <div className="flex items-center gap-3">
                          <Calendar className="w-4 h-4 text-primary" />
                          <div>
                            <p className="text-sm font-medium">{se.session_title}</p>
                            {se.start_time && <p className="text-xs text-muted-foreground">{new Date(se.start_time).toLocaleDateString()}</p>}
                          </div>
                        </div>
                        <Badge variant="outline" className={se.attended ? "text-green-600" : "text-muted-foreground"}>
                          {se.attended ? "Attended" : "Registered"}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit User Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Edit className="w-5 h-5" /> Edit User</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Full Name</Label><Input value={editForm.full_name || ""} onChange={e => setEditForm({...editForm, full_name: e.target.value})} data-testid="edit-name" /></div>
              <div><Label>Role</Label>
                <Select value={editForm.role || "learner"} onValueChange={v => setEditForm({...editForm, role: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="learner">Learner</SelectItem>
                    <SelectItem value="trainer">Trainer</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="tenant_admin">Business Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Department</Label><Input value={editForm.department || ""} onChange={e => setEditForm({...editForm, department: e.target.value})} /></div>
                <div><Label>Job Title</Label><Input value={editForm.job_title || ""} onChange={e => setEditForm({...editForm, job_title: e.target.value})} /></div>
              </div>
              <div><Label>Phone</Label><Input value={editForm.phone || ""} onChange={e => setEditForm({...editForm, phone: e.target.value})} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={showResetPwd} onOpenChange={setShowResetPwd}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-amber-500" /> Reset Password</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">A temporary password will be generated and sent to <strong>{u?.email}</strong>. The user will be required to change it on next login.</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowResetPwd(false)}>Cancel</Button>
              <Button onClick={handleResetPassword} disabled={saving} data-testid="confirm-reset-pwd">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}Send Reset Email
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Enroll in Course Modal */}
        <Dialog open={showEnrollModal} onOpenChange={setShowEnrollModal}>
          <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-primary" /> Enroll {u?.full_name?.split(" ")[0]} in Courses
              </DialogTitle>
            </DialogHeader>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search courses..."
                value={courseSearch}
                onChange={e => setCourseSearch(e.target.value)}
                className="pl-9"
                data-testid="course-search-input"
              />
            </div>
            {selectedCourses.size > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <Badge className="bg-primary/10 text-primary">{selectedCourses.size} selected</Badge>
                <button className="text-xs text-muted-foreground hover:text-primary underline" onClick={() => setSelectedCourses(new Set())}>Clear all</button>
              </div>
            )}
            <div className="flex-1 overflow-y-auto min-h-0 space-y-1.5 -mx-1 px-1" style={{ maxHeight: "380px" }}>
              {coursesLoading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
              ) : (() => {
                const enrolledIds = new Set(enrollments.map(e => e.course_id));
                const filtered = allCourses.filter(c =>
                  c.title?.toLowerCase().includes(courseSearch.toLowerCase()) ||
                  c.category?.toLowerCase().includes(courseSearch.toLowerCase())
                );
                if (filtered.length === 0) return <p className="text-center py-8 text-sm text-muted-foreground">No courses found</p>;
                return filtered.map(c => {
                  const isEnrolled = enrolledIds.has(c.id);
                  const isSelected = selectedCourses.has(c.id);
                  return (
                    <button
                      key={c.id}
                      disabled={isEnrolled}
                      onClick={() => !isEnrolled && toggleCourseSelection(c.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${
                        isEnrolled ? "opacity-50 cursor-not-allowed bg-muted/30" :
                        isSelected ? "border-primary bg-primary/5 ring-1 ring-primary/20" :
                        "hover:bg-muted/50 cursor-pointer"
                      }`}
                      data-testid={`course-option-${c.id}`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                        isEnrolled ? "bg-green-500/10" : isSelected ? "bg-primary" : "bg-muted"
                      }`}>
                        {isEnrolled ? <CheckCircle2 className="w-4 h-4 text-green-500" /> :
                         isSelected ? <Check className="w-4 h-4 text-white" /> :
                         <BookOpen className="w-4 h-4 text-muted-foreground" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{c.title}</p>
                        <p className="text-xs text-muted-foreground">{c.category || "General"} · {c.course_type?.includes("scorm") ? "eLearning" : c.course_type || "Course"}</p>
                      </div>
                      {isEnrolled && <Badge variant="outline" className="text-green-600 text-[10px] shrink-0">Enrolled</Badge>}
                    </button>
                  );
                });
              })()}
            </div>
            <DialogFooter className="border-t pt-3">
              <Button variant="outline" onClick={() => setShowEnrollModal(false)}>Cancel</Button>
              <Button
                onClick={handleEnrollCourses}
                disabled={selectedCourses.size === 0 || enrolling}
                data-testid="confirm-enroll-btn"
              >
                {enrolling ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <GraduationCap className="w-4 h-4 mr-1" />}
                Enroll in {selectedCourses.size || 0} Course{selectedCourses.size !== 1 ? "s" : ""}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default EmployeeProfilePage;
